export function clear() {
    localStorage.removeItem("authToken");
    localStorage.removeItem("authTokenExpiresAt");
    localStorage.removeItem("user");

    return;
}

export function isAuthorized() {
    const expiresAt = localStorage.getItem("authTokenExpiresAt");

    if (expiresAt && Date.now() < expiresAt) {
        return true;
    }

    clear();

    return false;
}

export function getUser() {
    const userStr = localStorage.getItem("user");
    const user = JSON.parse(userStr);

    return user;
}

export function getBrokerFromLocalStorage() {
    let broker = localStorage.getItem("broker");
    if (!broker) {
        broker = "paper"
    }

    return broker;
}

export function setBrokerInLocalStorage(value) {
    localStorage.setItem("broker", value);

    return true;
}

export function getAutoRefreshIntervalFromLocalStorage() {
    let autoRefreshInterval = localStorage.getItem("autoRefreshInterval");
    if (!autoRefreshInterval) {
        autoRefreshInterval = 5
    }

    return autoRefreshInterval;
}

export function setAutoRefreshIntervalInLocalStorage(value) {
    localStorage.setItem("autoRefreshInterval", value);

    return true;
}

export function getAccountBalanceFromLocalStorage() {
    try {
        let balance = localStorage.getItem("accountBalance");
        if (!balance) {
            balance = "0";
        }
        const parsedBalance = parseFloat(balance);
        return isNaN(parsedBalance) ? 0 : parsedBalance;
    } catch (error) {
        console.error('Error getting account balance from localStorage:', error);
        return 0;
    }
}

export function setAccountBalanceInLocalStorage(value) {
    try {
        const numericValue = parseFloat(value);
        if (isNaN(numericValue)) {
            console.error('Invalid balance value:', value);
            return false;
        }
        localStorage.setItem("accountBalance", numericValue.toString());
        return true;
    } catch (error) {
        console.error('Error setting account balance in localStorage:', error);
        return false;
    }
}

// New balance management functions
export function updateBalanceOnPositionEntry(entryPrice, quantity, marginMultiplier = 1.0) {
    const currentBalance = getAccountBalanceFromLocalStorage();
    const marginRequired = entryPrice * quantity * marginMultiplier;
    
    if (marginRequired > currentBalance) {
        throw new Error(`Insufficient balance. Required: ₹${marginRequired.toLocaleString()}, Available: ₹${currentBalance.toLocaleString()}`);
    }
    
    const newBalance = currentBalance - marginRequired;
    setAccountBalanceInLocalStorage(newBalance);
    
    return {
        previousBalance: currentBalance,
        newBalance: newBalance,
        marginUsed: marginRequired,
        remainingBalance: newBalance
    };
}

export function updateBalanceOnPositionExit(entryPrice, exitPrice, quantity, marginMultiplier = 1.0) {
    const currentBalance = getAccountBalanceFromLocalStorage();
    const marginUsed = entryPrice * quantity * marginMultiplier;
    const pnl = (exitPrice - entryPrice) * quantity;
    const newBalance = currentBalance + marginUsed + pnl;
    
    setAccountBalanceInLocalStorage(newBalance);
    
    return {
        previousBalance: currentBalance,
        newBalance: newBalance,
        marginReturned: marginUsed,
        pnl: pnl,
        totalChange: marginUsed + pnl
    };
}

export function getAvailableBalance() {
    return getAccountBalanceFromLocalStorage();
}

export function canAffordPosition(entryPrice, quantity, marginMultiplier = 1.0) {
    const currentBalance = getAccountBalanceFromLocalStorage();
    const marginRequired = entryPrice * quantity * marginMultiplier;
    return currentBalance >= marginRequired;
}

export function getBalanceHistory() {
    try {
        const history = localStorage.getItem("balanceHistory");
        return history ? JSON.parse(history) : [];
    } catch (error) {
        console.error('Error parsing balance history:', error);
        return [];
    }
}

export function addBalanceHistoryEntry(entry) {
    const history = getBalanceHistory();
    const newEntry = {
        ...entry,
        timestamp: new Date().toISOString(),
        id: Date.now(),
        // Ensure all numeric fields have default values
        marginUsed: entry.marginUsed || 0,
        totalChange: entry.totalChange || 0,
        pnl: entry.pnl || 0,
        previousBalance: entry.previousBalance || 0,
        newBalance: entry.newBalance || 0
    };
    history.push(newEntry);
    
    // Keep only last 100 entries
    if (history.length > 100) {
        history.splice(0, history.length - 100);
    }
    
    localStorage.setItem("balanceHistory", JSON.stringify(history));
    return newEntry;
}

export function getFormattedBalanceHistory(limit = 10) {
    const history = getBalanceHistory();
    return history
        .slice(-limit)
        .reverse()
        .map(entry => ({
            ...entry,
            formattedTimestamp: new Date(entry.timestamp || Date.now()).toLocaleString(),
            formattedAmount: entry.type === 'position_entry' 
                ? `-₹${(entry.marginUsed || 0).toLocaleString()}` 
                : `+₹${(entry.totalChange || 0).toLocaleString()}`,
            formattedPnl: entry.pnl ? `₹${(entry.pnl || 0).toLocaleString()}` : null
        }));
}

export function resetAccountBalance(newBalance = 10000) {
    setAccountBalanceInLocalStorage(newBalance);
    localStorage.removeItem("balanceHistory");
    return newBalance;
}

export function getBalanceSummary() {
    const currentBalance = getAccountBalanceFromLocalStorage();
    const history = getBalanceHistory();
    
    const totalEntries = history.filter(h => h.type === 'position_entry').length;
    const totalExits = history.filter(h => h.type === 'position_exit').length;
    const totalPnL = history
        .filter(h => h.pnl)
        .reduce((sum, h) => sum + h.pnl, 0);
    
    return {
        currentBalance,
        totalEntries,
        totalExits,
        totalPnL,
        openPositions: totalEntries - totalExits
    };
}

// Global User Account Management Functions
export function getSelectedUserAccountFromLocalStorage() {
    try {
        const selectedAccount = localStorage.getItem("selectedUserAccount");
        return selectedAccount ? JSON.parse(selectedAccount) : null;
    } catch (error) {
        console.error('Error getting selected user account from localStorage:', error);
        return null;
    }
}

export function setSelectedUserAccountInLocalStorage(userAccount) {
    try {
        localStorage.setItem("selectedUserAccount", JSON.stringify(userAccount));
        return true;
    } catch (error) {
        console.error('Error setting selected user account in localStorage:', error);
        return false;
    }
}

export function getSelectedUserAccountIdFromLocalStorage() {
    const selectedAccount = getSelectedUserAccountFromLocalStorage();
    return selectedAccount ? selectedAccount.id : null;
}

export function clearSelectedUserAccount() {
    localStorage.removeItem("selectedUserAccount");
    return true;
}
